import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, IconButton, Menu, MenuItem, Snackbar, Tooltip } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { supportedLanguages, type SupportedLanguage } from '../i18n';
import { useSaveLocalePreference } from '@hooks/useSaveLocalePreference';
import { toDisplayError } from '../utils/apiError';

function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const save = useSaveLocalePreference();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // REV-798: signed in, the choice is also saved to the account so it follows
  // the person to the next page load and sign-in; signed out it only switches.
  const handleLanguageChange = (langCode: SupportedLanguage) => {
    handleClose();
    setSaveError(null);
    save(langCode).catch((e: unknown) => {
      setSaveError(toDisplayError(e, t('settings.appearance.languageSaveFailed')));
    });
  };

  const currentLanguage = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;
  const currentLanguageInfo = supportedLanguages[currentLanguage] || supportedLanguages.en;

  return (
    <>
      <Tooltip title={t('languageSelector.tooltip', 'Change language')}>
        <IconButton
          onClick={handleClick}
          color="inherit"
          aria-label={t(
            'languageSelector.buttonLabel',
            'Current language: {{language}}. Change language.',
            {
              language: currentLanguageInfo.name,
            }
          )}
          aria-haspopup="true"
          aria-expanded={open}
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      {/* REV-525 (ARIA-04): aria-label on <Menu> lands on the popover paper,
          not the MenuList that carries role="menu" — it must go through
          MenuListProps. Also moved off a hardcoded English string, which was a
          particularly poor look inside the language switcher. */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-label': t('languageSelector.menuLabel', 'Language selection') }}
      >
        {Object.entries(supportedLanguages).map(([code, { name, nativeName }]) => (
          <MenuItem
            key={code}
            onClick={() => handleLanguageChange(code as SupportedLanguage)}
            selected={currentLanguage === code}
            lang={code}
          >
            {nativeName} ({name})
          </MenuItem>
        ))}
      </Menu>
      {/* role="alert" so a failed save is announced; the language the person
          picked stays applied. */}
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={8000}
        onClose={() => setSaveError(null)}
      >
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default LanguageSelector;
